import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { units, buildings, workorders, contractors } from '../services/api';
import toast from 'react-hot-toast';

const INITIAL_FORM = {
  unit_number: '', building_id: '', owner_name: '', owner_email: '', owner_phone: '',
  floor: '', bedrooms: '', bathrooms: '', square_footage: '', monthly_fee: '',
  move_in_date: '', is_rented: false, parking_stall: '', notes: '',
};

const STATUS_COLORS = {
  submitted: 'bg-yellow-100 text-yellow-800',
  acknowledged: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-600',
};

export default function Units() {
  const [unitList, setUnitList] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('units'); // 'units' | 'maintenance'
  const [filterBuilding, setFilterBuilding] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [showFeeModal, setShowFeeModal] = useState(null); // unit object
  const [feeMonths, setFeeMonths] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [contractorList, setContractorList] = useState([]);
  const [woModal, setWoModal] = useState(null);       // maintenance request object
  const [woForm, setWoForm] = useState({});
  const [creatingWo, setCreatingWo] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, bRes] = await Promise.all([
        units.list(filterBuilding ? { building_id: filterBuilding } : {}),
        buildings.list(),
      ]);
      setUnitList(uRes.data);
      setBuildingList(bRes.data);

      const [sRes, mRes] = await Promise.all([
        units.stats(filterBuilding ? { building_id: filterBuilding } : {}),
        units.maintenance(filterBuilding ? { building_id: filterBuilding } : {}),
      ]);
      setStats(sRes.data);
      setMaintenance(mRes.data);
    } catch {
      toast.error('Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterBuilding]);

  useEffect(() => {
    contractors.list().then((r) => setContractorList(r.data)).catch(() => {});
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...INITIAL_FORM, building_id: filterBuilding || buildingList[0]?.id || '' });
    setShowForm(true);
  };

  const openEdit = (unit) => {
    setEditing(unit);
    setForm({
      unit_number: unit.unit_number,
      building_id: unit.building_id,
      owner_name: unit.owner_name || '',
      owner_email: unit.owner_email || '',
      owner_phone: unit.owner_phone || '',
      floor: unit.floor || '',
      bedrooms: unit.bedrooms || '',
      bathrooms: unit.bathrooms || '',
      square_footage: unit.square_footage || '',
      monthly_fee: unit.monthly_fee || '',
      move_in_date: unit.move_in_date || '',
      is_rented: unit.is_rented || false,
      parking_stall: unit.parking_stall || '',
      notes: unit.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        floor: form.floor ? parseInt(form.floor) : null,
        bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null,
        bathrooms: form.bathrooms ? parseFloat(form.bathrooms) : null,
        square_footage: form.square_footage ? parseInt(form.square_footage) : null,
        monthly_fee: form.monthly_fee ? parseFloat(form.monthly_fee) : null,
      };
      if (editing) {
        await units.update(editing.id, payload);
        toast.success('Unit updated');
      } else {
        await units.create(payload);
        toast.success('Unit created');
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save unit');
    }
  };

  const handleDelete = async (unit) => {
    if (!window.confirm(`Delete Unit ${unit.unit_number}? This cannot be undone.`)) return;
    try {
      await units.delete(unit.id);
      toast.success('Unit deleted');
      load();
    } catch {
      toast.error('Failed to delete unit');
    }
  };

  const handleGenerateFees = async () => {
    if (!showFeeModal) return;
    setGenerating(true);
    try {
      const today = new Date();
      const res = await units.generateFees(showFeeModal.id, {
        months: feeMonths,
        start_month: today.getMonth() + 1,
        start_year: today.getFullYear(),
      });
      toast.success(`Generated ${res.data.generated} fee record${res.data.generated !== 1 ? 's' : ''}`);
      setShowFeeModal(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate fees');
    } finally {
      setGenerating(false);
    }
  };

  const handleMaintenanceUpdate = async (reqId, newStatus) => {
    try {
      await units.updateMaintenance(reqId, { status: newStatus });
      toast.success('Status updated');
      load();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const openWoModal = (req) => {
    setWoModal(req);
    setWoForm({
      title: req.title,
      description: req.description || '',
      priority: req.priority === 'urgent' ? 'urgent' : req.priority || 'medium',
      building_id: req.building_id,
      unit_number: req.unit_number || '',
      contractor_id: '',
      notes: `Created from maintenance request #${req.id}\nSubmitted by owner of unit ${req.unit_number || ''}`,
    });
  };

  const handleCreateWo = async (e) => {
    e.preventDefault();
    if (!woModal) return;
    setCreatingWo(true);
    try {
      // 1. Create the work order
      const woRes = await workorders.create({
        ...woForm,
        building_id: parseInt(woForm.building_id),
        contractor_id: woForm.contractor_id ? parseInt(woForm.contractor_id) : null,
      });
      const newWo = woRes.data;

      // 2. Link it back to the maintenance request + set status to in_progress
      await units.updateMaintenance(woModal.id, {
        work_order_id: newWo.id,
        status: 'in_progress',
      });

      toast.success(`Work order WO-${String(newWo.id).padStart(4, '0')} created!`);
      setWoModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create work order');
    } finally {
      setCreatingWo(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Units & Owner Portal</h1>
          <p className="text-gray-500 text-sm mt-1">Manage units, assign owners, collect strata fees</p>
        </div>
        <button onClick={openNew}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Unit
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total Units', value: stats.total_units, color: 'blue' },
            { label: 'Occupied', value: stats.occupied_units, color: 'green' },
            { label: 'Vacant', value: stats.vacant_units, color: 'gray' },
            { label: 'Pending Fees', value: stats.pending_fees_count, color: 'yellow' },
            { label: 'Outstanding', value: `$${stats.pending_fees_amount.toFixed(0)}`, color: 'red' },
            { label: 'Open Requests', value: stats.open_maintenance_requests, color: 'purple' },
          ].map(({ label, value, color }) => (
            <div key={label} className={`bg-white rounded-xl border border-gray-200 p-4 text-center`}>
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className={`text-xl font-bold text-${color}-600`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs & Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[['units', 'Units'], ['maintenance', 'Maintenance Requests']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                tab === key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {label}
              {key === 'maintenance' && maintenance.filter(r => r.status === 'submitted').length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                  {maintenance.filter(r => r.status === 'submitted').length}
                </span>
              )}
            </button>
          ))}
        </div>

        <select value={filterBuilding} onChange={(e) => setFilterBuilding(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:border-blue-500 outline-none">
          <option value="">All Buildings</option>
          {buildingList.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>

        <p className="text-xs text-gray-400 ml-auto">
          Owner portal: <a href="https://app.getstratapulse.com/owner/login" target="_blank" rel="noreferrer" className="font-mono text-blue-600 hover:underline">app.getstratapulse.com/owner/login</a>
        </p>
      </div>

      {/* Units tab */}
      {tab === 'units' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {unitList.length === 0 ? (
            <div className="py-16 text-center">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3" />
              </svg>
              <p className="text-gray-500">No units yet</p>
              <button onClick={openNew} className="mt-2 text-blue-600 hover:underline text-sm font-medium">
                Add your first unit
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    {['Unit', 'Building', 'Owner', 'Monthly Fee', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {unitList.map((unit) => (
                    <tr key={unit.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold text-gray-900">
                        {unit.unit_number}
                        {unit.parking_stall && (
                          <p className="text-xs font-normal text-gray-400">P{unit.parking_stall}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{unit.building_name}</td>
                      <td className="px-4 py-3">
                        {unit.owner_name ? (
                          <div>
                            <p className="font-medium text-gray-900">{unit.owner_name}</p>
                            <p className="text-xs text-gray-400">{unit.owner_email}</p>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Unassigned</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {unit.monthly_fee ? `$${unit.monthly_fee}/mo` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          unit.owner_id
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {unit.owner_id ? 'Portal Active' : 'No Account'}
                        </span>
                        {unit.is_rented && (
                          <span className="ml-1 text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700">
                            Rented
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => openEdit(unit)}
                            className="text-xs text-blue-600 hover:underline">Edit</button>
                          {unit.monthly_fee && (
                            <button onClick={() => setShowFeeModal(unit)}
                              className="text-xs text-green-600 hover:underline">Fees</button>
                          )}
                          <button onClick={() => handleDelete(unit)}
                            className="text-xs text-red-500 hover:underline">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Maintenance tab */}
      {tab === 'maintenance' && (
        <div className="space-y-3">
          {maintenance.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm py-16 text-center text-gray-400">
              No maintenance requests
            </div>
          ) : maintenance.map((req) => (
            <div key={req.id} className={`bg-white rounded-xl border shadow-sm p-5 ${
              req.work_order_id ? 'border-blue-200' : 'border-gray-200'
            }`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{req.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[req.status] || 'bg-gray-100 text-gray-600'}`}>
                      {req.status.replace('_', ' ')}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      req.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                      req.priority === 'high'   ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {req.priority}
                    </span>
                    {req.work_order_id && (
                      <Link
                        to="/workorders"
                        className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                      >
                        📋 WO-{String(req.work_order_id).padStart(4, '0')}
                      </Link>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {req.building_name}
                    {req.unit_number ? ` · Unit ${req.unit_number}` : ''}
                    {' · '}
                    {new Date(req.created_at).toLocaleDateString('en-CA', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })}
                    {' · '}<span className="capitalize">{req.category?.replace('_', ' ')}</span>
                  </p>
                  {req.description && (
                    <p className="text-sm text-gray-600 mt-2">{req.description}</p>
                  )}
                  {req.manager_notes && (
                    <p className="text-xs text-blue-600 mt-1 italic">Note: {req.manager_notes}</p>
                  )}
                </div>

                {/* Right-side controls */}
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <select
                    value={req.status}
                    onChange={(e) => handleMaintenanceUpdate(req.id, e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white focus:border-blue-500 outline-none">
                    <option value="submitted">Submitted</option>
                    <option value="acknowledged">Acknowledged</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="closed">Closed</option>
                  </select>

                  {/* Create WO button — only if no WO yet */}
                  {!req.work_order_id ? (
                    <button
                      onClick={() => openWoModal(req)}
                      className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-1 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create Work Order
                    </button>
                  ) : (
                    <Link
                      to="/workorders"
                      className="text-xs px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg font-medium border border-green-200 transition-colors"
                    >
                      ✓ View Work Order
                    </Link>
                  )}
                </div>
              </div>

              {req.photo_urls?.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {req.photo_urls.map((url, i) => (
                    <img key={i} src={url} alt="" className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Generate Fees Modal */}
      {showFeeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Generate Strata Fees</h2>
            <p className="text-gray-500 text-sm mb-4">
              Unit {showFeeModal.unit_number} · ${showFeeModal.monthly_fee}/month
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                How many months ahead?
              </label>
              <select value={feeMonths} onChange={(e) => setFeeMonths(parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm">
                {[1,2,3,6,12].map(n => (
                  <option key={n} value={n}>{n} month{n !== 1 ? 's' : ''}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Starting from current month. Skips months already generated.
              </p>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowFeeModal(null)}
                className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleGenerateFees} disabled={generating}
                className="flex-1 bg-green-600 text-white py-2.5 rounded-lg hover:bg-green-700 font-medium disabled:opacity-60">
                {generating ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Work Order Modal */}
      {woModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Create Work Order</h2>
                <p className="text-xs text-gray-500 mt-0.5">From maintenance request · {woModal.building_name}</p>
              </div>
              <button onClick={() => setWoModal(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateWo} className="p-6 space-y-4">
              {/* Source request summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-1">From Maintenance Request</p>
                <p className="text-sm text-blue-900 font-medium">{woModal.title}</p>
                <p className="text-xs text-blue-700 mt-0.5">
                  {woModal.building_name}
                  {woModal.unit_number ? ` · Unit ${woModal.unit_number}` : ''}
                  {' · '}{woModal.category?.replace('_', ' ')}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Work Order Title *</label>
                <input
                  required
                  value={woForm.title || ''}
                  onChange={(e) => setWoForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={woForm.description || ''}
                  onChange={(e) => setWoForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={woForm.priority || 'medium'}
                    onChange={(e) => setWoForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Unit Number</label>
                  <input
                    value={woForm.unit_number || ''}
                    onChange={(e) => setWoForm(f => ({ ...f, unit_number: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none"
                    placeholder="e.g. 201"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Assign Contractor (optional)</label>
                <select
                  value={woForm.contractor_id || ''}
                  onChange={(e) => setWoForm(f => ({ ...f, contractor_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none"
                >
                  <option value="">— Unassigned —</option>
                  {contractorList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.company ? ` · ${c.company}` : ''}{c.specialty ? ` (${c.specialty})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Internal Notes</label>
                <textarea
                  rows={2}
                  value={woForm.notes || ''}
                  onChange={(e) => setWoForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none resize-none"
                />
              </div>

              <div className="bg-gray-50 rounded-lg px-4 py-3 text-xs text-gray-600 space-y-1">
                <p>✓ Work order will be created with status <strong>Requested</strong></p>
                <p>✓ Maintenance request status will change to <strong>In Progress</strong></p>
                <p>✓ Work order will be linked back to this request</p>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setWoModal(null)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingWo}
                  className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {creatingWo
                    ? <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg> Creating…</>
                    : '📋 Create Work Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Unit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editing ? `Edit Unit ${editing.unit_number}` : 'Add New Unit'}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Unit Number *</label>
                  <input required value={form.unit_number} onChange={(e) => set('unit_number', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none"
                    placeholder="101" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Building *</label>
                  <select required value={form.building_id} onChange={(e) => set('building_id', parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none">
                    <option value="">Select building</option>
                    {buildingList.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">Owner Information</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Owner Name</label>
                  <input value={form.owner_name} onChange={(e) => set('owner_name', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none"
                    placeholder="Jane Smith" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Owner Email</label>
                  <input type="email" value={form.owner_email} onChange={(e) => set('owner_email', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none"
                    placeholder="jane@example.com" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Owner Phone</label>
                <input value={form.owner_phone} onChange={(e) => set('owner_phone', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none"
                  placeholder="604-555-0100" />
              </div>

              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">Unit Details</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Floor</label>
                  <input type="number" value={form.floor} onChange={(e) => set('floor', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Bedrooms</label>
                  <input type="number" value={form.bedrooms} onChange={(e) => set('bedrooms', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Bathrooms</label>
                  <input type="number" step="0.5" value={form.bathrooms} onChange={(e) => set('bathrooms', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Sq. Footage</label>
                  <input type="number" value={form.square_footage} onChange={(e) => set('square_footage', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Monthly Strata Fee ($)</label>
                  <input type="number" step="0.01" value={form.monthly_fee} onChange={(e) => set('monthly_fee', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none"
                    placeholder="350.00" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Parking Stall</label>
                  <input value={form.parking_stall} onChange={(e) => set('parking_stall', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none"
                    placeholder="A-12" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Move-in Date</label>
                  <input type="date" value={form.move_in_date} onChange={(e) => set('move_in_date', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_rented" checked={form.is_rented}
                  onChange={(e) => set('is_rented', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600" />
                <label htmlFor="is_rented" className="text-sm text-gray-700">Unit is currently rented out</label>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none resize-none" />
              </div>

              {form.owner_email && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-700">
                  ℹ️ The owner can register at <strong>/owner/login</strong> using this email address to access their portal.
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700 font-medium">
                  {editing ? 'Save Changes' : 'Add Unit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
